import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { ReactNode } from 'react';

import { ApiError, api, isAbortError } from '../api';
import type { ChatAccount } from '../types';

export interface AccountValue {
  account: ChatAccount | null;
  loading: boolean;
  busy: boolean;
  error: unknown | null;
  clearError: () => void;
  refresh: () => Promise<void>;
  register: (username: string, password: string) => Promise<ChatAccount>;
  login: (username: string, password: string) => Promise<ChatAccount>;
  rename: (username: string) => Promise<ChatAccount>;
  logout: () => Promise<void>;
  recordListening: (source: 'live' | 'archive', seconds?: number) => Promise<void>;
  adopt: (account: ChatAccount | null) => void;
}

const AccountContext = createContext<AccountValue | null>(null);

export function useAccount(): AccountValue {
  const value = useContext(AccountContext);
  if (!value) throw new Error('useAccount() was called outside <AccountProvider>.');
  return value;
}

export function AccountProvider({ children }: { children: ReactNode }) {
  const [account, setAccount] = useState<ChatAccount | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown | null>(null);
  const heartbeatBusyRef = useRef(false);
  const accountRef = useRef<ChatAccount | null>(account);
  accountRef.current = account;

  const adopt = useCallback((next: ChatAccount | null) => setAccount(next), []);
  const clearError = useCallback(() => setError(null), []);

  const refresh = useCallback(async (): Promise<void> => {
    try {
      const next = await api.getRadioAccount();
      setAccount(next);
      setError(null);
    } catch (caught) {
      if (caught instanceof ApiError && caught.isUnauthorised) {
        setAccount(null);
        return;
      }
      throw caught;
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    api.getRadioAccount(controller.signal)
      .then((next) => setAccount(next))
      .catch((caught: unknown) => {
        if (isAbortError(caught)) return;
        if (caught instanceof ApiError && caught.isUnauthorised) {
          setAccount(null);
          return;
        }
        setError(caught);
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, []);

  const register = useCallback(async (username: string, password: string): Promise<ChatAccount> => {
    if (busy) throw new Error('Account request already in progress.');
    setBusy(true);
    setError(null);
    try {
      const next = await api.registerChatAccount({ username, password });
      setAccount(next);
      return next;
    } catch (caught) {
      setError(caught);
      throw caught;
    } finally {
      setBusy(false);
    }
  }, [busy]);

  const login = useCallback(async (username: string, password: string): Promise<ChatAccount> => {
    if (busy) throw new Error('Account request already in progress.');
    setBusy(true);
    setError(null);
    try {
      const next = await api.loginChatAccount({ username, password });
      setAccount(next);
      return next;
    } catch (caught) {
      setError(caught);
      throw caught;
    } finally {
      setBusy(false);
    }
  }, [busy]);

  const logout = useCallback(async (): Promise<void> => {
    if (busy) throw new Error('Account request already in progress.');
    setBusy(true);
    setError(null);
    try {
      await api.logoutChatAccount();
      setAccount(null);
    } catch (caught) {
      setError(caught);
      throw caught;
    } finally {
      setBusy(false);
    }
  }, [busy]);

  const rename = useCallback(async (username: string): Promise<ChatAccount> => {
    if (busy) throw new Error('Account request already in progress.');
    setBusy(true);
    setError(null);
    try {
      const next = await api.renameRadioAccount(username);
      setAccount(next);
      return next;
    } catch (caught) {
      setError(caught);
      throw caught;
    } finally {
      setBusy(false);
    }
  }, [busy]);

  const recordListening = useCallback(async (
    source: 'live' | 'archive',
    seconds = 30,
  ): Promise<void> => {
    if (!accountRef.current || heartbeatBusyRef.current) return;
    heartbeatBusyRef.current = true;
    try {
      const next = await api.recordRadioListening({ source, seconds });
      setAccount(next);
    } catch (caught) {
      if (caught instanceof ApiError && caught.isUnauthorised) setAccount(null);
      // Listening credit must never interrupt playback or show a global error.
    } finally {
      heartbeatBusyRef.current = false;
    }
  }, []);

  const value = useMemo<AccountValue>(() => ({
    account,
    loading,
    busy,
    error,
    clearError,
    refresh,
    register,
    login,
    rename,
    logout,
    recordListening,
    adopt,
  }), [
    account,
    adopt,
    busy,
    clearError,
    error,
    loading,
    login,
    rename,
    logout,
    recordListening,
    refresh,
    register,
  ]);

  return <AccountContext.Provider value={value}>{children}</AccountContext.Provider>;
}
